import { HTTPMethod } from "http-method-enum";
import { ReasonPhrases, StatusCodes } from "http-status-codes";

import PhotoReviewsServiceClient from "clients/PhotoReviewsServiceClient";
import { ReviewsSort, defaultFitCounts } from "rtr-constants";
import { withCdnCaching, CacheDurations } from "helpers/cache-control-helpers";
import { extractQueryObjectFromUrl } from "../../../../../assets/javascripts/helpers/location-helpers";

// After we upgrade next to a version >= v13.2 we can export custom route handlers per method type, like:
// export async function GET(req) {}
// @see {https://nextjs.org/blog/next-13-2#custom-route-handlers}
export default async function handler(req, res) {
  if (req.method !== HTTPMethod.GET) {
    res.status(StatusCodes.METHOD_NOT_ALLOWED).send(ReasonPhrases.METHOD_NOT_ALLOWED);
    return;
  }

  await handleReviews(req, res);
}

///////////////////////////////////////////
//                                       //
//  Business Logic for fetching reviews  //
//                                       //
///////////////////////////////////////////

const pageSize = 40;
const defaultSort = ReviewsSort.FEATURED;

function getFitCounts(reviewsSummary) {
  // unfortunately, these keys are hard-coded in the backend
  const { fitCounts = {} } = reviewsSummary || {};
  const combinedFitCounts = {
    ...defaultFitCounts,
    ...fitCounts,
  };

  return Object.fromEntries(Object.entries(combinedFitCounts).filter(([k]) => k in defaultFitCounts));
}

function getFitRating(fitCounts) {
  let fitRating = null;
  const fitRatingTotal = Object.values(fitCounts).reduce((a, b) => a + b, 0);

  if (fitRatingTotal > 0) {
    fitRating = fitCounts.Large / fitRatingTotal - fitCounts.Small / fitRatingTotal;
  }

  return fitRating;
}

export function getReviewsWithPhotos(moments) {
  if (!(Array.isArray(moments) && moments.length > 0)) {
    return [];
  }

  return moments.filter(o => Array.isArray(o?.photoIds) && o.photoIds.length > 0) || [];
}

export async function getReviewsAndSummary(productId, sizes = [], sort = ReviewsSort.FEATURED, page = 1) {
  const [dataResponse, summaryResponse] = await Promise.allSettled([
    PhotoReviewsServiceClient.getInstance().fetchMomentWithCount({
      status: "approved",
      limit: pageSize,
      offset: (page - 1) * pageSize,
      orderBy: sort,
      sizeWorn: sizes,
      styleName: productId,
    }),
    PhotoReviewsServiceClient.getInstance().fetchReviewSummary(productId),
  ]);

  const { value: summary, status: summaryStatus } = summaryResponse;
  const { value: data, status: dataStatus } = dataResponse;

  const { averageStarRating: averageRating, reviewCount: count } = summary;

  const currentCount = Math.min(data?.totalCount ?? 0, pageSize);
  const fitCounts = getFitCounts(summary);
  const fitRating = getFitRating(fitCounts);

  return {
    averageRating: averageRating ?? 0,
    count: count ?? 0,
    filteredCount: data?.totalCount ?? 0,
    currentCount,
    data: data?.moments ?? [],
    reviewsWithPhotos: getReviewsWithPhotos(data?.moments),
    dataStatus,
    fitRating,
    loading: false,
    page,
    perPage: pageSize,
    productId,
    sort,
    summary,
    summaryStatus,
  };
}

async function handleReviews(req, res) {
  // productId is not a URL param, but a path param, and is only available via
  // req.query
  const requestQuery = req.query;
  const { productId } = requestQuery;
  const sizes = requestQuery["sizes[]"] || [];

  // unfortunately, req.query returns a flat object of URL params, as wlm is
  // nested, therefore we have to unpack the URL a little more manually
  const { page: pageValue, sort = defaultSort } = extractQueryObjectFromUrl(req.url);

  const page = parseInt(pageValue, 10) || 1;

  try {
    const response = await getReviewsAndSummary(productId, sizes, sort, page);
    withCdnCaching(res, req, { "s-maxage": CacheDurations.LONG });

    res.status(StatusCodes.OK).json(response);
  } catch (e) {
    console.error(`Failed to fetch reviews for ${productId}`);
    console.error(e);
    res.status(StatusCodes.INTERNAL_SERVER_ERROR).send(`Failed to fetch reviews for ${productId}`);
  }
}
